import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { submitToHubSpot, HS_FORM_CHAPTER } from "@/lib/hubspot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterCapture() {
  const [email, setEmail] = useState("");
  const [firstname, setFirstname] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Email is required.");
    setLoading(true);
    try {
      // Fire in parallel: HubSpot (list capture) + backend (chapter delivery)
      const hs = submitToHubSpot({
        formId: HS_FORM_CHAPTER,
        pageName: "Preview Chapter — thedecisionmargin.com",
        fields: [
          { name: "email", value: email },
          { name: "firstname", value: firstname },
        ],
      });
      const local = axios.post(`${API}/newsletter`, {
        email,
        name: firstname,
        source: "book",
      });
      const results = await Promise.allSettled([hs, local]);
      const anyOk = results.some((r) => r.status === "fulfilled");
      if (!anyOk) {
        throw new Error("Both HubSpot and backend submissions failed");
      }

      setDone(true);
      toast.success("Preview chapter emailed. Download starting…");
      const a = document.createElement("a");
      a.href = `${API}/preview-chapter.pdf`;
      a.download = "Decision_Margin_Preview_Chapter.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="newsletter-capture"
      className="mt-14 border border-[#262626] bg-[#0e0e0e]"
    >
      <div className="grid lg:grid-cols-12">
        <div className="lg:col-span-5 p-8 lg:p-10 bg-[#d92d20] text-white">
          <div className="overline mb-4" style={{ color: "rgba(255,255,255,0.85)" }}>
            Free · Preview Chapter
          </div>
          <h3 className="font-display font-extrabold text-3xl sm:text-4xl tracking-[-0.025em] leading-[0.95]">
            Read the first
            <br />
            chapter free.
          </h3>
          <p className="mt-5 text-sm leading-relaxed text-white/85 max-w-xs">
            Join the reader list. Get a preview chapter of Decision Margin plus
            occasional notes on leadership under pressure.
          </p>
        </div>

        <div className="lg:col-span-7 p-8 lg:p-10">
          {done ? (
            <div data-testid="newsletter-success">
              <div className="overline overline-red mb-3">{"You\u2019re in"}</div>
              <h4 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight leading-tight">
                Preview chapter on the way.
              </h4>
              <p className="mt-3 text-white/65 text-sm leading-relaxed">
                {"The preview chapter is downloading now and has also been emailed to you. If it doesn\u2019t arrive, check promotions or spam."}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <label className="block">
                  <span className="overline block mb-2">Name (optional)</span>
                  <input
                    data-testid="newsletter-name"
                    className="brut-input"
                    placeholder="Your first name"
                    value={firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-2">
                    Email <span className="text-[#d92d20]">*</span>
                  </span>
                  <input
                    data-testid="newsletter-email"
                    type="email"
                    required
                    className="brut-input"
                    placeholder="you@organization.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-white/40 max-w-xs leading-relaxed">
                  No spam. Unsubscribe anytime. Delivered via Kismet Leads.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="inline-flex items-center justify-center gap-3 bg-[#d92d20] hover:bg-[#b91d13] disabled:opacity-60 text-white px-7 py-4 text-[12px] font-semibold tracking-[0.18em] uppercase transition-colors w-full sm:w-auto"
                >
                  {loading ? "Sending…" : "Send My Chapter →"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
